import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { validateEmail } from '@src/utils/checkValid';
import actions from '@src/redux/actions';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import CommonButton from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import { useTranslation } from 'react-i18next';
import { COLOR } from '@src/styles/color';
import CommonSwitch from '@src/new-components/Switch';
import {
  RELATED_UNIT_CODE_LENGTH,
  RELATED_UNIT_CODE_REGEX,
} from '@src/constants';
import { StyledNewUIInvoice } from './index.style';
import NewFormExportInvoice from './NewFormExportInvoice';

const NewUIInvoice = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { latestOrder } = useSelector((state) => state.user);

  const [isChecked, setIsChecked] = useState(false);
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const { invoice, error } = useSelector((state) => state.order);
  const [draftInvoice, setDraftInvoice] = useState(invoice);

  useEffect(() => {
    if (latestOrder?.invoice?.companyTax) {
      const {
        companyName,
        companyAddress,
        companyTax,
        companyEmail,
        relatedUnitCode,
      } = latestOrder.invoice;
      dispatch(actions.order.updateHasInvoice(true));
      dispatch(
        actions.order.updateInvoice({
          companyName,
          companyAddress,
          companyTax,
          companyEmail,
          relatedUnitCode,
        }),
      );
    }
  }, [latestOrder]);

  const handleCloseInvoiceDialog = () => {
    if (!invoice?.companyTax && isChecked) {
      setIsChecked(false);
      dispatch(actions.order.updateHasInvoice(false));
    }
    setOpenInvoiceDialog(false);
    setDraftInvoice(invoice);
    dispatch(actions.order.updateErrorInvoice({}));
  };

  const handleConfirmExportInvoice = () => {
    const verifyInvoice = () => {
      const {
        companyName,
        companyAddress,
        companyTax,
        companyEmail,
        relatedUnitCode,
      } = draftInvoice;
      const errors = {};

      if (!companyName.trim()) errors.companyName = 'companyNameRequired';
      if (companyName?.trim() && companyName?.trim().length < 2)
        errors.companyName = 'companyNameInvalid';
      if (!companyAddress?.trim())
        errors.companyAddress = 'companyAddressRequired';
      if (!companyTax?.trim()) errors.companyTax = 'companyTaxRequired';
      if (!companyEmail?.trim()) errors.companyEmail = 'emailRequired';
      if (companyEmail && !validateEmail(companyEmail))
        errors.companyEmail = 'emailInvalid';
      const relatedUnitCodeTrim = relatedUnitCode?.trim();
      if (relatedUnitCodeTrim) {
        if (
          !RELATED_UNIT_CODE_REGEX.test(relatedUnitCodeTrim) ||
          relatedUnitCodeTrim.length !== RELATED_UNIT_CODE_LENGTH
        ) {
          errors.relatedUnitCode = 'relatedUnitCodeInvalid';
        }
      }

      return errors;
    };
    const invoiceError = verifyInvoice();
    if (Object.values(invoiceError).length > 0) {
      dispatch(actions.order.updateErrorInvoice(invoiceError));
    } else {
      const relatedUnitCodeTrim = draftInvoice?.relatedUnitCode?.trim();
      if (relatedUnitCodeTrim)
        draftInvoice.relatedUnitCode = relatedUnitCodeTrim;
      else draftInvoice.relatedUnitCode = undefined;
      dispatch(actions.order.updateInvoice(draftInvoice));
      setOpenInvoiceDialog(false);
    }
  };

  const renderActionsInvoiceDialog = () => (
    <>
      <CommonButton
        customStyle={{
          padding: '8px 24px',
          borderRadius: '8px',
          height: '40px',
        }}
        variant="outlineSmall"
        textProps={{
          variantText: 'body-14-semibold',
          color: COLOR.darkBlue,
        }}
        onClick={handleCloseInvoiceDialog}
      >
        {t('cancel')}
      </CommonButton>
      <CommonButton
        customStyle={{
          padding: '8px 24px',
          borderRadius: '8px',
          height: '40px',
        }}
        textProps={{
          variantText: 'body-14-semibold',
          color: COLOR.indigo.default,
        }}
        onClick={handleConfirmExportInvoice}
        variant="contained"
      >
        {t('confirmExportInvoice')}
      </CommonButton>
    </>
  );

  const handleChangeCheckedInvoice = () => {
    const value = !isChecked;
    dispatch(actions.order.updateHasInvoice(value));
    setIsChecked(value);
    if (!isChecked) {
      setOpenInvoiceDialog(true);
    }
  };

  const handleChangeInvoice = (event) => {
    const { name, value } = event.target;
    dispatch(actions.order.updateErrorInvoice({ ...error, [name]: null }));
    setDraftInvoice({ ...draftInvoice, [name]: value });
  };

  useEffect(() => {
    if (invoice.companyTax) {
      setIsChecked(true);
      setDraftInvoice(invoice);
    }
  }, [invoice?.companyTax]);

  return (
    <StyledNewUIInvoice>
      <div className="new-ui-invoice-wrapper">
        <div className="new-ui-invoice-content">
          <Typography variant="body-16-regular" color={COLOR.descriptionGray}>
            {isChecked && invoice?.companyTax
              ? t('invoiceRequested')
              : t('requestInvoice')}
          </Typography>
          {isChecked && invoice?.companyTax && (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a
              onClick={() => setOpenInvoiceDialog(true)}
              href="#"
              className="edit-here-link"
            >
              {t('editHere')}
            </a>
          )}
        </div>
        <CommonSwitch
          variant="ios"
          checked={isChecked}
          onChange={handleChangeCheckedInvoice}
        />
      </div>
      <CommonDialog
        width="512px"
        title={t('infoExportInvoice')}
        fullWidth
        contentComponent={
          <NewFormExportInvoice
            handleChangeInvoice={handleChangeInvoice}
            disableInvoice={isChecked}
            draftInvoice={draftInvoice}
          />
        }
        styleTitle={{
          height: '56px',
        }}
        open={openInvoiceDialog}
        onClose={handleCloseInvoiceDialog}
        actionComponent={renderActionsInvoiceDialog()}
      />
    </StyledNewUIInvoice>
  );
};

export default NewUIInvoice;
